"use client";

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastOptions {
  id?: string;
  duration?: number;
  undo?: () => void;
  undoLabel?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  undo?: () => void;
  undoLabel?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number;
}

interface ToastContextType {
  toast: {
    success: (message: string, options?: ToastOptions) => string;
    error: (message: string, options?: ToastOptions) => string;
    info: (message: string, options?: ToastOptions) => string;
    warning: (message: string, options?: ToastOptions) => string;
    dismiss: (id: string) => void;
  };
}

const ToastContext = createContext<ToastContextType | null>(null);

let globalToastDispatcher: ((item: ToastItem) => void) | null = null;
let globalToastDismiss: ((id: string) => void) | null = null;

export const globalToast = {
  success: (message: string, options?: ToastOptions) => {
    const id = options?.id || Math.random().toString(36).substring(2, 9);
    globalToastDispatcher?.({
      id,
      type: "success",
      message,
      duration: options?.duration ?? 4500,
      undo: options?.undo,
      undoLabel: options?.undoLabel,
      action: options?.action,
      createdAt: Date.now(),
    });
    return id;
  },
  error: (message: string, options?: ToastOptions) => {
    const id = options?.id || Math.random().toString(36).substring(2, 9);
    globalToastDispatcher?.({
      id,
      type: "error",
      message,
      duration: options?.duration ?? 5500,
      undo: options?.undo,
      undoLabel: options?.undoLabel,
      action: options?.action,
      createdAt: Date.now(),
    });
    return id;
  },
  info: (message: string, options?: ToastOptions) => {
    const id = options?.id || Math.random().toString(36).substring(2, 9);
    globalToastDispatcher?.({
      id,
      type: "info",
      message,
      duration: options?.duration ?? 4000,
      undo: options?.undo,
      undoLabel: options?.undoLabel,
      action: options?.action,
      createdAt: Date.now(),
    });
    return id;
  },
  warning: (message: string, options?: ToastOptions) => {
    const id = options?.id || Math.random().toString(36).substring(2, 9);
    globalToastDispatcher?.({
      id,
      type: "warning",
      message,
      duration: options?.duration ?? 5000,
      undo: options?.undo,
      undoLabel: options?.undoLabel,
      action: options?.action,
      createdAt: Date.now(),
    });
    return id;
  },
  dismiss: (id: string) => {
    globalToastDismiss?.(id);
  },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((item: ToastItem) => {
    setToasts((prev) => [item, ...prev.slice(0, 4)]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    globalToastDispatcher = addToast;
    globalToastDismiss = dismissToast;
    return () => {
      globalToastDispatcher = null;
      globalToastDismiss = null;
    };
  }, [addToast, dismissToast]);

  const toastMethods = {
    success: (message: string, options?: ToastOptions) => globalToast.success(message, options),
    error: (message: string, options?: ToastOptions) => globalToast.error(message, options),
    info: (message: string, options?: ToastOptions) => globalToast.info(message, options),
    warning: (message: string, options?: ToastOptions) => globalToast.warning(message, options),
    dismiss: dismissToast,
  };

  return (
    <ToastContext.Provider value={{ toast: toastMethods }}>
      {children}
      {/* Toast Render Viewport */}
      <div
        aria-live="polite"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 z-50 flex flex-col gap-2.5 max-w-[calc(100vw-2rem)] w-96 pointer-events-none print:hidden"
      >
        <AnimatePresence>
          {toasts.map((t) => (
            <ToastCard key={t.id} item={t} onDismiss={() => dismissToast(t.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (item.duration <= 0) return;
    if (isPaused) return;

    timerRef.current = setTimeout(() => {
      onDismiss();
    }, item.duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [item.duration, isPaused, onDismiss]);

  const icons = {
    success: <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />,
    error: <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
    info: <Info className="w-4 h-4 text-blue-500 shrink-0" />,
  };

  const borders = {
    success: "border-emerald-500/30 dark:border-emerald-500/20 shadow-emerald-500/5",
    error: "border-rose-500/30 dark:border-rose-500/20 shadow-rose-500/5",
    warning: "border-amber-500/30 dark:border-amber-500/20 shadow-amber-500/5",
    info: "border-blue-500/30 dark:border-blue-500/20 shadow-blue-500/5",
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={`pointer-events-auto relative overflow-hidden rounded-xl border bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-3.5 shadow-xl transition-all ${borders[item.type]}`}
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className="mt-0.5">{icons[item.type]}</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-800 dark:text-slate-100 leading-snug break-words">
              {item.message}
            </p>

            {/* Actions: Undo / Custom Action */}
            {(item.undo || item.action) && (
              <div className="flex items-center gap-2 mt-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                {item.undo && (
                  <button
                    type="button"
                    onClick={() => {
                      item.undo?.();
                      onDismiss();
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline active:scale-95 cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{item.undoLabel || "Undo"}</span>
                  </button>
                )}
                {item.action && (
                  <button
                    type="button"
                    onClick={() => {
                      item.action?.onClick();
                      onDismiss();
                    }}
                    className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline active:scale-95 cursor-pointer"
                  >
                    {item.action.label}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress countdown bar */}
      {item.duration > 0 && !isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-100 dark:bg-slate-800">
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: item.duration / 1000, ease: "linear" }}
            className={`h-full ${
              item.type === "success"
                ? "bg-emerald-500"
                : item.type === "error"
                ? "bg-rose-500"
                : item.type === "warning"
                ? "bg-amber-500"
                : "bg-blue-500"
            }`}
          />
        </div>
      )}
    </motion.div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback to global dispatch if used outside provider
    return {
      toast: globalToast,
    };
  }
  return context;
}
